import { Component, ElementRef, EventEmitter, HostListener, Input, Optional, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlContainer, NG_VALUE_ACCESSOR, ControlValueAccessor, FormGroup, FormControl } from '@angular/forms';

@Component({
  selector: 'app-mprx-input',
  templateUrl: './mprx-input.component.html',
  styleUrls: ['./mprx-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MprxInputComponent),
      multi: true,
    },
  ],
})
export class MprxInputComponent implements ControlValueAccessor {
  @Input() label: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() maxlength?: any;
  @Input() IsRequired: boolean = false;
  @Input() ShowSearchIcon: boolean = false;
  @Input() EnableSuggestions = false;
  @Input() FilteredData!: any[]
  @Input() formControlName?: any;
  @Input() fieldName: string = '';
  @Input() AllowOnlyNumbers: boolean = false;
  @Input() UsePhoneFormat: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() IsDrugNotFound  = false;
  @Input() IsPharmacyNotFound = false;
  @Input() phoneNumExists: any;
  @Output() loadMoreSuggestions = new EventEmitter<void>();
  @Output() input = new EventEmitter<{ event: Event, field: string }>();
  @Output() getSelectedInfo = new EventEmitter<Event>();
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  showSuggestions: boolean = false;
  filteredSuggestions: string[] = [];
  @Input() hasMoreData = true;
  @Input() TotalDrugCount!: number;
 @Output() blurEvent = new EventEmitter<string>();
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  value: string = '';
  drugNotFound: any;
  isLoadingSuggestions = false;
  startY: number = 0;

constructor(@Optional() private controlContainer: ControlContainer, private elementRef: ElementRef) {}

@HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
  if (!this.elementRef.nativeElement.contains(event.target)) {
    this.showSuggestions = false;
    this.FilteredData = [];
  }
  
}

writeValue(value: any): void {
  this.value = value || '';
  this.onChange(this.value);
}

registerOnChange(fn: (value: any) => void): void {
  this.onChange = fn;
}

registerOnTouched(fn: () => void): void {
  this.onTouched = fn;
}
setDisabledState(isDisabled: boolean): void {
  this.isDisabled = isDisabled;
}

onInput(event: Event): void {
  let value = (event.target as HTMLInputElement).value;
  if (this.AllowOnlyNumbers) {
    const rawValue = value?.replace(/\D/g, '');
    value = rawValue;
    if (this.UsePhoneFormat && rawValue.length === 10) {
      value = `(${rawValue.substring(0, 3)}) ${rawValue.substring(3, 6)}-${rawValue.substring(6)}`;
    }
    this.value = value;
    this.inputElement.nativeElement.value = value;
    this.onChange(rawValue);
  } else {
    this.value = value;
    this.inputElement.nativeElement.value = value;
    this.onChange(value);
  }
  this.input.emit({ event, field: this.fieldName });
  this.showSuggestions = this.FilteredData && value ? true : false;
}

onSearchClicked(): void {
  if (this.value?.trim()) {
    this.value = '';
    this.inputElement.nativeElement.value = '';
    this.onChange('');
    this.showSuggestions = false;
    this.FilteredData = [];
    this.input.emit({ event: new Event('input'), field: this.fieldName });
  }
}

closeSuggestions(): void {
  this.showSuggestions = false;
  this.FilteredData = [];
}

onKeyDown(event: KeyboardEvent): void {
  if (this.AllowOnlyNumbers) {
    const allowedKeys = [
      'Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'
    ];
    const isNumber = /^[0-9]$/.test(event.key);
    if (!isNumber && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }
}
onBlur(): void {
  this.onTouched();
  const enteredText = this.inputElement?.nativeElement?.value?.trim() || '';
  this.blurEvent.emit(enteredText);
}

selectSuggestion($event: PointerEvent, suggestion: any): void {
  if ($event.pointerType === 'touch' && Math.abs($event.movementY) > 5) {
    return;
  }
  $event.preventDefault();
  this.value = this.fieldName === "DrugName" ?  (suggestion?.drugname + ' ' + suggestion?.strength + ' ' + suggestion?.drugformname).toLowerCase() : suggestion?.pharmName;
  this.onChange(this.value);
  this.getSelectedInfo.emit(suggestion);
  this.inputElement.nativeElement.value = this.value;
  this.FilteredData = [];
  this.showSuggestions = false;
  this.onBlur();
}

isFieldInvalid(controlName: string): boolean {
  const control = (this.controlContainer?.control as FormGroup)?.get(controlName);
  return !!(control && control.invalid && control.touched);
}

ngOnChanges(){
  this.resetScrollLoading()
}
onDrugSuggestionScroll(event: any): void {
  const target = event.target;
  const scrollTop = target.scrollTop;
  const scrollHeight = target.scrollHeight;
  const offsetHeight = target.offsetHeight;

  const threshold = 50; // pixels from bottom
  const isNearBottom = scrollTop + offsetHeight >= scrollHeight - threshold;

  if (isNearBottom && !this.isLoadingSuggestions && this.hasMoreData && (this.FilteredData.length !== this.TotalDrugCount)) {
        this.isLoadingSuggestions = true;
    this.loadMoreSuggestions.emit();

  }
}
public resetScrollLoading(): void {
      this.isLoadingSuggestions = false;
}
handlePointer(ev: PointerEvent, suggestion: any) {
  if ((ev.pointerType === 'mouse' || ev.pointerType === 'pen') && ev.type === 'pointerdown') {
    this.selectSuggestion(ev, suggestion);
  }

  if (ev.pointerType === 'touch' && ev.type === 'pointerup') {
    // guard against scroll
    if (Math.abs((ev.clientY ?? 0) - this.startY) > 8) return;
    this.selectSuggestion(ev, suggestion);
  }

  if (ev.pointerType === 'touch' && ev.type === 'pointerdown') {
    this.startY = ev.clientY ?? 0;
  }
}
}